'use client';

import React, { useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CheckIcon,
  ClipboardTextIcon,
  FolderOpenIcon,
} from '@phosphor-icons/react';
import { QuestionBarScopeChip, QuestionBarFileTypeScopeChip } from '@tranquility-ai/theme-components';
import { Button } from '@tranquility-ai/theme-components/button';
import '@tranquility-ai/theme-components/popover.css';
import { Breadcrumbs } from '@tranquility-ai/theme-components/breadcrumbs';
import '@tranquility-ai/theme-components/breadcrumbs.css';
import './evidence-locker-tree.css';
import '@tranquility-ai/theme-components/menu.css';
import { FeaturesContext } from '@/app/contexts';
import { useCasesList } from '@/hooks/useCasesList';
import {
  archiveContainerSourceKey,
  extractPathAfterBulk,
  folderPathForEvidenceLockerDisplay,
  getCaseDisplayName,
  removeMD5Hash,
  stripUnwantedLeadingPathSegments,
} from '@/shared/functions';
import type { ScopesObject } from '@/shared/types';
import { caseFolderLabels, relabelCaseFolderSegments } from '@/shared/case-summary';
import {
  archiveContainerPrefix,
  pathToBreadcrumbItems,
} from '@/shared/evidence-locker-breadcrumbs';
import { getCurrentFileInfo } from '@/shared/clientFunctions';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import { iconForFileName } from '@/shared/provenance-tree-ui';
import {
  useChatEvidenceLockerNew,
  type LockerBrowseContainer,
} from './chat-evidence-locker-context-new';
import {
  applyEvidenceCategoryScopeCycle,
  evidenceFileTypeLabel,
} from './evidence-locker-ui-shared-new';
import { removeScopedItem } from '@/shared/timeline-scope-items';
import { toggleScopeInclusion } from '@/shared/toggle-scope-inclusion';
import { PATH_DISPLAY_WHITESPACE_CLASSNAME } from '@/shared/uiConstants';
import { useTimePilotT } from '@/i18n/useTimePilotT';

export function hasQuestionBarScopeContent(
  scopesAndFiles: ScopesObject,
  contentCategoriesEnabled: boolean,
): boolean {
  return (
    hasChipSelection(scopesAndFiles) ||
    (contentCategoriesEnabled &&
      ((scopesAndFiles.evidenceCategoryScopes?.length ?? 0) > 0 ||
        (scopesAndFiles.excludedEvidenceCategoryScopes?.length ?? 0) > 0))
  );
}

function hasChipSelection(scopesAndFiles: ScopesObject): boolean {
  return (
    (scopesAndFiles.files?.length ?? 0) > 0 ||
    (scopesAndFiles.scopes?.length ?? 0) > 0 ||
    (scopesAndFiles.excludedFiles?.length ?? 0) > 0 ||
    (scopesAndFiles.excludedScopes?.length ?? 0) > 0 ||
    (scopesAndFiles.excludedEvidenceCategoryScopes?.length ?? 0) > 0
  );
}

function CopyTooltipContent({
  fileOrScope,
  scopesAndFiles,
  resolveScopeDisplay,
  hideText = false,
}: {
  fileOrScope: string;
  scopesAndFiles: ScopesObject;
  resolveScopeDisplay: (scopeTag: string) => string;
  hideText?: boolean;
}) {
  const t = useTimePilotT();
  const [copied, setCopied] = useState(false);
  const isScope =
    scopesAndFiles.scopes?.includes(fileOrScope) ||
    scopesAndFiles.excludedScopes?.includes(fileOrScope);

  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const extracted = extractPathAfterBulk(fileOrScope).truncatedFilePath;
    const textToCopy = extracted || fileOrScope || '';
    void navigator.clipboard?.writeText(textToCopy).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1000);
    });
  };

  const displayText = isScope
    ? resolveScopeDisplay(fileOrScope)
    : extractPathAfterBulk(fileOrScope, true).truncatedFilePath;

  return (
    <>
      {hideText ? null : (
        <span className={`min-w-0 break-all ${PATH_DISPLAY_WHITESPACE_CLASSNAME}`}>{displayText}</span>
      )}
      <Button
        type="button"
        iconOnly
        size="sm"
        style={copied ? 'solid' : 'text'}
        color={copied ? 'accent' : 'light'}
        background="inverse"
        aria-label={t(copied ? 'chat.evidence.copied' : 'chat.evidence.copyPath')}
        onClick={handleCopy}
      >
        {copied ? <CheckIcon /> : <ClipboardTextIcon weight="duotone" />}
      </Button>
    </>
  );
}

function parentBrowsePath(path: string): string {
  const parts = path.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  parts.pop();
  return parts.join('/');
}

function pathPreviewPosition(anchor: HTMLElement, card: HTMLElement) {
  const rect = anchor.getBoundingClientRect();
  const cardRect = card.getBoundingClientRect();
  const margin = 8;
  const gap = 4;
  let top = rect.top - cardRect.height - gap;
  if (top < margin) top = rect.bottom + gap;
  let left = rect.left;
  const maxLeft = window.innerWidth - margin - cardRect.width;
  if (left > maxLeft) left = Math.max(margin, maxLeft);
  return { left, top };
}

function pathMenuIsOpen(card: HTMLElement | null) {
  return Boolean(card?.querySelector('[aria-expanded="true"]'));
}

function EvidencePathChip({
  fileOrScope,
  isScope,
  caseId,
  caseLabels,
  label,
  isExcluded,
  scopesAndFiles,
  resolveScopeDisplay,
  onToggle,
  onRemove,
  onBrowse,
  onPathMenuOpenChange,
}: {
  fileOrScope: string;
  isScope: boolean;
  caseId: string;
  caseLabels: Map<string, string>;
  label: string;
  isExcluded: boolean;
  scopesAndFiles: ScopesObject;
  resolveScopeDisplay: (scopeTag: string) => string;
  onToggle: () => void;
  onRemove: () => void;
  onBrowse: (path: string, container?: LockerBrowseContainer | null) => void;
  onPathMenuOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [previewPos, setPreviewPos] = useState({ left: 0, top: 0 });
  const { cellId, accessToken } = useCellServiceSession();
  const hideTimer = useRef<number | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const pathMenuReported = useRef(false);
  const reportPathMenu = (isOpen: boolean) => {
    if (pathMenuReported.current === isOpen) return;
    pathMenuReported.current = isOpen;
    onPathMenuOpenChange?.(isOpen);
  };

  const show = () => {
    if (hideTimer.current != null) window.clearTimeout(hideTimer.current);
    setOpen(true);
  };
  // The ellipsis opens a portaled path menu. Keep this preview mounted while
  // that menu is open so moving onto it does not unmount the breadcrumbs.
  const hide = () => {
    if (hideTimer.current != null) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      if (pathMenuIsOpen(cardRef.current)) {
        hide();
        return;
      }
      setOpen(false);
    }, 250);
  };

  const browsePath = isScope
    ? stripUnwantedLeadingPathSegments(folderPathForEvidenceLockerDisplay(fileOrScope, caseId))
    : extractPathAfterBulk(fileOrScope, true).truncatedFilePath || fileOrScope;
  const { items: pathItems } = pathToBreadcrumbItems(browsePath, null, caseLabels);
  const items = pathItems.map((item, index) => {
    const isFileLeaf = !isScope && index === pathItems.length - 1;
    const segmentName = item.id.split('/').pop() ?? item.label;
    const Icon = isFileLeaf ? iconForFileName(segmentName) : FolderOpenIcon;
    return {
      ...item,
      icon: <Icon size={16} weight="regular" aria-hidden />,
    };
  });

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const anchor = anchorRef.current;
      const card = cardRef.current;
      if (!anchor || !card) return;
      const next = pathPreviewPosition(anchor, card);
      setPreviewPos((prev) => (prev.left === next.left && prev.top === next.top ? prev : next));
    };
    update();
    const card = cardRef.current;
    const observer = new ResizeObserver(update);
    if (card) observer.observe(card);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, items.length, label]);

  useLayoutEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (anchorRef.current?.contains(target)) return;
      if (cardRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest('[role="menu"]')) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);

  useLayoutEffect(() => {
    return () => {
      if (hideTimer.current != null) window.clearTimeout(hideTimer.current);
      if (pathMenuReported.current) {
        pathMenuReported.current = false;
        onPathMenuOpenChange?.(false);
      }
    };
  }, [onPathMenuOpenChange]);

  useLayoutEffect(() => {
    if (!open) {
      reportPathMenu(false);
      return;
    }
    const card = cardRef.current;
    if (!card) return;
    const sync = () => reportPathMenu(pathMenuIsOpen(card));
    const observer = new MutationObserver(sync);
    observer.observe(card, { subtree: true, attributes: true, attributeFilter: ['aria-expanded'] });
    return () => observer.disconnect();
  }, [open, onPathMenuOpenChange]);

  const preview =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={cardRef}
            role="dialog"
            aria-label={label}
            className="fixed z-[100008] flex w-max min-w-[12rem] max-w-80 items-center gap-2 rounded-lg bg-[var(--color-ink-085,#0f1d32)] p-2 text-[var(--text-inverse,#ffffff)] shadow-lg"
            style={{ left: previewPos.left, top: previewPos.top }}
            onMouseEnter={show}
            onMouseLeave={hide}
            onClickCapture={(event) => {
              // The path menu is portaled outside the question bar, so opening
              // it would otherwise drop the bar's focus and collapse it.
              const button = event.target instanceof Element ? event.target.closest('button') : null;
              if (!button || (button.textContent ?? '').trim() !== '…') return;
              reportPathMenu(button.getAttribute('aria-expanded') !== 'true');
            }}
          >
            {items.length > 0 ? (
              <Breadcrumbs
                className="evidence-locker-breadcrumbs"
                tone="dark"
                aria-label={label}
                items={items}
                onNavigate={(item, index) => {
                  const target = !isScope && index === items.length - 1 ? parentBrowsePath(item.id) : item.id;
                  setOpen(false);
                  // The original path, not the display path: extractPathAfterBulk
                  // drops a leading files/<hash>/, which makes a processed extract
                  // look like a virtual zip member.
                  const containerKey = archiveContainerSourceKey(fileOrScope);
                  const containerPrefix = containerKey ? archiveContainerPrefix(browsePath) : null;
                  const insideContainer =
                    !!containerPrefix &&
                    (target === containerPrefix || target.startsWith(`${containerPrefix}/`));
                  if (!containerKey || !insideContainer || !containerPrefix) {
                    onBrowse(target);
                    return;
                  }
                  void (async () => {
                    const containerInfo = await getCurrentFileInfo(containerKey, cellId, accessToken);
                    const fileStatusId = containerInfo?.fileStatusId?.trim() ?? '';
                    onBrowse(
                      target,
                      fileStatusId
                        ? { searchablePath: containerPrefix, fileStatusId }
                        : null,
                    );
                  })();
                }}
              />
            ) : null}
            <CopyTooltipContent
              fileOrScope={fileOrScope}
              scopesAndFiles={scopesAndFiles}
              resolveScopeDisplay={resolveScopeDisplay}
              hideText
            />
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <span
        ref={anchorRef}
        className="inline-flex max-w-full"
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        <QuestionBarScopeChip
          label={label}
          kind={isScope ? 'folder' : 'file'}
          state={isExcluded ? 'excluded' : 'included'}
          onToggle={onToggle}
          onRemove={onRemove}
        />
      </span>
      {preview}
    </>
  );
}

export type EvidenceSelectionChipsLayout = "composer" | "embedded";

export type EvidenceSelectionChipsNewProps = {
  /** `composer` keeps the legacy light-blue shell below ChatComposerNew; `embedded` is for QuestionBar's dark scope slot. */
  layout?: EvidenceSelectionChipsLayout;
  /** Fired when a chip's collapsed-path menu opens or closes. */
  onPathMenuOpenChange?: (open: boolean) => void;
};

export function EvidenceSelectionChipsNew({
  layout = "composer",
  onPathMenuOpenChange,
}: EvidenceSelectionChipsNewProps = {}) {
  const t = useTimePilotT();
  const { caseId, scopesAndFiles, setScopesAndFiles, requestBrowse } = useChatEvidenceLockerNew();
  const featuresContext = useContext(FeaturesContext);
  const contentCategoriesEnabled = !!featuresContext?.featureFlags?.contentCategories;
  const casesQuery = useCasesList();
  const barRef = useRef<HTMLDivElement>(null);

  const selectedContentCategories = useMemo(
    () => scopesAndFiles.evidenceCategoryScopes ?? [],
    [scopesAndFiles.evidenceCategoryScopes],
  );

  const excludedContentCategories = useMemo(
    () => scopesAndFiles.excludedEvidenceCategoryScopes ?? [],
    [scopesAndFiles.excludedEvidenceCategoryScopes],
  );

  const fileTypeChipItems = useMemo(
    () => [
      ...excludedContentCategories.map((fileType) => ({
        fileType,
        state: 'excluded' as const,
      })),
      ...selectedContentCategories.map((fileType) => ({
        fileType,
        state: 'included' as const,
      })),
    ],
    [excludedContentCategories, selectedContentCategories],
  );

  const scopeLabelByTag = useMemo(() => {
    if (!casesQuery.data?.length) return {} as Record<string, string>;
    const m: Record<string, string> = {};
    for (const c of casesQuery.data) {
      const tag = c.caseTag?.trim();
      if (!tag) continue;
      m[tag] = getCaseDisplayName(c.caseName?.trim() ? c.caseName.trim() : tag);
    }
    return m;
  }, [casesQuery.data]);

  // For folder scopes *inside* a case, whose display path leads with the case's
  // `folder_token` segment. Case-tag scopes are already named by the map above.
  const caseFolderLabelMap = useMemo(
    () => caseFolderLabels(casesQuery.data),
    [casesQuery.data],
  );

  const resolveScopeDisplay = useCallback(
    (scopeTag: string) => {
      const t = scopeTag.trim();
      const fromMap = scopeLabelByTag[t] ?? scopeLabelByTag[scopeTag];
      if (fromMap) return fromMap;
      const list = casesQuery.data;
      if (list?.length) {
        for (const c of list) {
          const ct = c.caseTag?.trim();
          if (!ct) continue;
          if (ct === t || ct === scopeTag) {
            return getCaseDisplayName(c.caseName?.trim() ? c.caseName.trim() : ct);
          }
        }
        const lastSeg = scopeTag.split('/').pop()?.trim();
        if (lastSeg && lastSeg !== scopeTag) {
          for (const c of list) {
            const ct = c.caseTag?.trim();
            if (ct && ct === lastSeg) {
              return getCaseDisplayName(c.caseName?.trim() ? c.caseName.trim() : ct);
            }
          }
        }
      }
      return relabelCaseFolderSegments(
        removeMD5Hash(
          stripUnwantedLeadingPathSegments(
            folderPathForEvidenceLockerDisplay(scopeTag, caseId),
          ),
        ),
        caseFolderLabelMap,
      );
    },
    [scopeLabelByTag, caseId, casesQuery.data, caseFolderLabelMap],
  );

  const toggleScopeOrFile = useCallback(
    (fileOrScope: string) => {
      setScopesAndFiles((prev) => toggleScopeInclusion(prev, fileOrScope));
    },
    [setScopesAndFiles],
  );

  const removeScopeOrFile = useCallback(
    (fileOrScope: string) => {
      setScopesAndFiles((prev) => {
        const kind =
          prev.files.includes(fileOrScope) || prev.excludedFiles.includes(fileOrScope)
            ? 'file'
            : 'scope';
        return removeScopedItem(prev, { kind, path: fileOrScope });
      });
    },
    [setScopesAndFiles],
  );

  const removeContentCategoryChip = useCallback(
    (fileType: string) => {
      setScopesAndFiles((prev) => removeScopedItem(prev, { kind: 'fileType', path: fileType }));
    },
    [setScopesAndFiles],
  );

  const toggleContentCategoryChip = useCallback(
    (fileType: string) => {
      setScopesAndFiles((prev) => ({
        ...prev,
        ...applyEvidenceCategoryScopeCycle(prev, fileType),
      }));
    },
    [setScopesAndFiles],
  );

  const chipItems = useMemo(
    () => [
      ...[...(scopesAndFiles.scopes ?? []), ...(scopesAndFiles.excludedScopes ?? [])].sort(
        (a, b) =>
          resolveScopeDisplay(a).localeCompare(resolveScopeDisplay(b), undefined, {
            sensitivity: 'base',
          }),
      ),
      ...[...(scopesAndFiles.files ?? []), ...(scopesAndFiles.excludedFiles ?? [])].sort(
        (a, b) => a.localeCompare(b),
      ),
    ],
    [scopesAndFiles, resolveScopeDisplay],
  );

  const showChips =
    hasChipSelection(scopesAndFiles) ||
    (contentCategoriesEnabled &&
      (selectedContentCategories.length > 0 || excludedContentCategories.length > 0));

  if (!showChips) return null;

  const chipRow = (
    <div
      ref={barRef}
      className={
        layout === "embedded"
          ? "custom-scrollbar2 box-border flex max-h-[142px] flex-wrap items-center justify-start gap-2 overflow-y-auto"
          : "custom-scrollbar2 box-border mt-[-4px] flex max-h-[142px] flex-wrap items-center justify-start gap-2 overflow-y-auto pt-2"
      }
    >
        {contentCategoriesEnabled &&
          fileTypeChipItems.map(({ fileType, state }) => (
              <QuestionBarFileTypeScopeChip
                key={`filetype-${fileType}-${state}`}
                label={evidenceFileTypeLabel(fileType, t)}
                state={state}
                onToggle={() => toggleContentCategoryChip(fileType)}
                onRemove={() => removeContentCategoryChip(fileType)}
              />
          ))}
        {chipItems.map((fileOrScope, index) => {
          const isScopeChip =
            scopesAndFiles.scopes?.includes(fileOrScope) ||
            scopesAndFiles.excludedScopes?.includes(fileOrScope);
          const isExcluded =
            scopesAndFiles.excludedFiles?.includes(fileOrScope) ||
            scopesAndFiles.excludedScopes?.includes(fileOrScope);
          const label = isScopeChip
            ? resolveScopeDisplay(fileOrScope)
            : removeMD5Hash(fileOrScope.split('/').pop() || '');

          return (
            <EvidencePathChip
              key={`${fileOrScope}-${index}`}
              fileOrScope={fileOrScope}
              isScope={isScopeChip}
              caseId={caseId}
              caseLabels={caseFolderLabelMap}
              label={label}
              isExcluded={isExcluded}
              scopesAndFiles={scopesAndFiles}
              resolveScopeDisplay={resolveScopeDisplay}
              onToggle={() => toggleScopeOrFile(fileOrScope)}
              onRemove={() => removeScopeOrFile(fileOrScope)}
              onBrowse={requestBrowse}
              onPathMenuOpenChange={onPathMenuOpenChange}
            />
          );
        })}
    </div>
  );

  if (layout === "embedded") {
    return chipRow;
  }

  return (
    <div className="box-border mt-[-28px] w-full rounded-b-3xl bg-[#99C4F6] px-2 pb-2 pt-[28px]">
      {chipRow}
    </div>
  );
}
